/**
 * PB-Detail · Wrapper page with sticky left sidebar + tab content.
 *
 * Mirrors the CRM 1.0 user profile structure (sidebar + 7 sub-tabs).
 * The first tab is the **Dashboard** (default view when opening a PB).
 * The URL reflects the active tab so deep-linking works.
 *
 * Tabs:
 *   dashboard         — PbDashboard (widget grid, configurable)
 *   profildaten       — existing PbProfileEdit (sections form)
 *   betreuungen       — cares + travels
 *   medien-dokumente  — media + documents
 *   kommunikation     — messages (full CRUD)
 *   aufgaben-infos   — tasks + infos (full CRUD; bis 23.10.2026 `aufgaben-notizen`,
 *                       der alte Slug bleibt als Alias gültig)
 *   bewertungen       — received + given ratings
 */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND_LIGHT, BRAND_DARK, getThemeColors } from '../../lib/theme';
import { buildPath } from '../../lib/route';
import { AppLink, isNewTabClick } from '../../lib/openLink';
import { PbDashboard } from './PbDashboard';
import { useIsMobile } from '../../lib/useBreakpoint';
import { usePermissions } from '../../contexts/PermissionsContext';
import { type V1User } from '../../lib/v1';
import { PbSidebar } from './profile/PbSidebar';
import { PbPhaseBar } from './profile/PbPhaseBar';
import { PbProfileEdit } from './profile/PbProfileEdit';
import { BetreuungenTab } from './tabs/BetreuungenTab';
import { MedienTab } from './tabs/MedienTab';
import { KommunikationTab } from './tabs/KommunikationTab';
import { ProtokollTab } from './tabs/ProtokollTab';
import { AufgabenNotizenTab } from './tabs/AufgabenNotizenTab';
import { BewertungenTab } from './tabs/BewertungenTab';
import { PbScheduleForm } from './tabs/PbScheduleForm';

export type PbTabId =
  | 'dashboard'
  | 'profildaten'
  | 'tagesplan'
  | 'betreuungen'
  | 'medien-dokumente'
  | 'kommunikation'
  | 'aufgaben-infos'
  | 'bewertungen'
  | 'protokoll';

export const PB_TABS: { id: PbTabId; labelKey: string }[] = [
  { id: 'dashboard',        labelKey: 'pflege.detail.tabs.overview' },
  { id: 'profildaten',      labelKey: 'pflege.detail.tabs.profile' },
  { id: 'tagesplan',        labelKey: 'pflege.detail.tabs.schedule' },
  { id: 'betreuungen',      labelKey: 'pflege.detail.tabs.cares' },
  { id: 'medien-dokumente', labelKey: 'pflege.detail.tabs.media' },
  { id: 'kommunikation',    labelKey: 'pflege.detail.tabs.communication' },
  { id: 'aufgaben-infos',   labelKey: 'pflege.detail.tabs.tasksInfos' },
  { id: 'bewertungen',      labelKey: 'pflege.detail.tabs.ratings' },
];

/**
 * Protokoll-Tab hängt an PB_TABS an, sobald der Admin `protokoll.read` hat
 * (bewusst nicht in PB_TABS, damit die Basisliste stabil bleibt).
 */
const PROTOKOLL_TAB: { id: PbTabId; labelKey: string } = { id: 'protokoll', labelKey: 'pflege.detail.tabs.protokoll' };
const PROTOKOLL_PERMISSION = 'protokoll.read';

/** Slug vor der Umbenennung „Aufgaben & Notizen" → „Aufgaben und Infos" (23.10.2026). */
const LEGACY_TASKS_TAB = 'aufgaben-notizen';

const VALID_TABS = new Set<string>([...PB_TABS.map(t => t.id), PROTOKOLL_TAB.id]);

/**
 * Tabs, die nur für Pflegeempfänger Sinn ergeben. Eine Kontaktperson
 * (Angehörige, Rechnungsempfänger, Hausarzt) bekommt denselben Profilaufbau,
 * aber ohne Tagesplan, Betreuungen, Medien & Dokumente und Bewertungen.
 */
const CARE_ONLY_TABS = new Set<PbTabId>(['tagesplan', 'betreuungen', 'medien-dokumente', 'bewertungen']);

interface Props {
  user: V1User | null;
  /** Active tab slug from URL. Anything unknown collapses to 'dashboard'. */
  tab: string | null;
  onClose: () => void;
  onSelectTab: (tab: PbTabId) => void;
  onSaved?: (u: V1User) => void;
  dark: boolean;
  /**
   * Pflegeempfänger? Bei `false` bleibt der Aufbau identisch, aber die
   * betreuungsbezogenen Teile entfallen: Tabs (s. CARE_ONLY_TABS),
   * Sidebar-Blöcke (Personenangaben/Startdatum/Nettolohn) und die
   * Dashboard-Widgets Jahresübersicht + Betreuungen.
   */
  careRecipient?: boolean;
  /**
   * Kunde (erscheint unter /pflege)? Erst dann entfällt der Prozessbalken —
   * vorher bleibt er sichtbar, auch in der letzten Stage.
   */
  isKunde?: boolean;
}

export function PbDetailPage({ user, tab, onClose, onSelectTab, onSaved, dark, careRecipient = true, isKunde = false }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const isMobile = useIsMobile();
  // Default to 'dashboard' (first tab) when no valid tab is given.
  // Alt-Slug 'stundenplan' (vor Umbenennung in 'tagesplan') bleibt als
  // Deep-Link-Alias gültig.
  const { hasPermission } = usePermissions();
  const canReadProtokoll = hasPermission(PROTOKOLL_PERMISSION);
  // Alt-Slug 'aufgaben-notizen' (bis 23.10.2026) → 'aufgaben-infos'.
  const normalizedTab = tab === 'stundenplan' ? 'tagesplan' : tab === LEGACY_TASKS_TAB ? 'aufgaben-infos' : tab;
  useEffect(() => {
    // Alte Deep-Links still auf die neue Adresse umschreiben (kein neuer Verlaufseintrag).
    if (tab !== LEGACY_TASKS_TAB) return;
    const next = window.location.pathname.replace(/\/aufgaben-notizen(?=\/|$)/, '/aufgaben-infos');
    if (next !== window.location.pathname) window.history.replaceState(null, '', next + (window.location.search ?? '') + (window.location.hash ?? ''));
  }, [tab]);
  // Ein Deep-Link auf einen ausgeblendeten Tab darf keine leere Seite zeigen —
  // er fällt wie ein unbekannter Slug aufs Dashboard zurück.
  const tabAllowed = (id: string): boolean =>
    VALID_TABS.has(id)
    && (id !== 'protokoll' || canReadProtokoll)
    && (careRecipient || !CARE_ONLY_TABS.has(id as PbTabId));
  const activeTab: PbTabId = normalizedTab && tabAllowed(normalizedTab)
    ? (normalizedTab as PbTabId)
    : 'dashboard';

  // T6: nach einem Phasenwechsel den Listen-Cache des Parents aktualisieren.
  const handlePhaseChange = (phase: number, phaseLabel: string | null) => {
    if (user?.id != null) onSaved?.({ ...user, phase, phase_label: phaseLabel } as V1User);
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: isMobile ? 'auto' : '100%',
      minHeight: 0,
    }}>

      {/* T6: Phasen-Stepper full-width an erster Stelle (Navbar trägt Zurück +
          Breadcrumb). "Anfrage abbrechen" lebt im ⋯-Menü der Personen-Zeile
          des Familienkontos (AccountEditToggle); bei abgebrochener Anfrage
          (negative Phase) erscheint hier ein Status-Badge. */}
      {/* Kein marginBottom auf dem Wrapper: sobald die Person Kunde ist,
          entfällt der Prozessbalken — ein Außenabstand hier würde eine leere
          Lücke über dem Grid hinterlassen. Der Abstand lebt deshalb IN der
          PbPhaseBar-Karte und verschwindet mit ihr. */}
      {user?.id != null && careRecipient && !isKunde && (
        <div style={{ flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <PbPhaseBar key={user.id} user={user} dark={dark} onPhaseChange={handlePhaseChange} />
            </div>
            {typeof user.phase === 'number' && user.phase < 0 && (
              <span style={{
                fontSize: 11, fontWeight: 600, borderRadius: 6, padding: '3px 9px',
                background: dark ? 'rgba(180,35,24,0.14)' : '#fef3f2',
                color: '#b42318', border: '1px solid #b4231855', whiteSpace: 'nowrap',
              }}>
                {tr('anfragen.abbruch.cancelled_badge')}
              </span>
            )}
          </div>
          {/* Bedingungsfelder rendert der PhaseRulesBlock innerhalb der
              PbPhaseBar (ersetzt das frühere PhaseRuleFields-Duplikat). */}
        </div>
      )}

      {/* Two-column grid (sidebar + content) on desktop; stacked on mobile */}
      <div style={{
        flex: isMobile ? undefined : 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 320px) minmax(0, 1fr)',
        gap: isMobile ? 14 : 24,
      }}>

        {/* Left: sticky sidebar (block on mobile) */}
        <aside style={{ overflowY: isMobile ? 'visible' : 'auto', minWidth: 0 }}>
          <PbSidebar user={user} dark={dark} careRecipient={careRecipient} onSaved={onSaved} />
        </aside>

        {/* Right: tab bar + body */}
        <section style={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
          <TabBar
            active={activeTab}
            onChange={onSelectTab}
            dark={dark}
            isMobile={isMobile}
            hasUser={user?.id != null}
            userId={user?.id ?? null}
            careRecipient={careRecipient}
          />
          {/* Scroll-Container für alle Tab-Inhalte. `dashboard` bringt einen
              eigenen inneren Auto-Scroll mit und `profildaten` (PbProfileEdit)
              scrollt über seinen mainRef — beide bleiben dadurch unverändert;
              die übrigen Tabs (Betreuungen, Medien, Kommunikation, Aufgaben &
              Notizen, Bewertungen) wurden sonst abgeschnitten. */}
          <div style={{ flex: 1, minHeight: 0, marginTop: isMobile ? 12 : 14, display: 'flex', flexDirection: 'column', overflowY: isMobile ? 'visible' : 'auto' }}>
            {activeTab === 'dashboard' && (
              <div style={{ flex: 1, minHeight: 0, overflowY: isMobile ? 'visible' : 'auto' }}>
                <PbDashboard
                  user={user}
                  dark={dark}
                  onSelectTab={onSelectTab}
                  careRecipient={careRecipient}
                  onUserSaved={onSaved}
                />
              </div>
            )}
            {activeTab === 'profildaten' && (
              <PbProfileEdit user={user} dark={dark} onClose={onClose} onSaved={onSaved} embedded careRecipient={careRecipient} />
            )}
            {activeTab === 'tagesplan' && (
              <PbScheduleForm user={user} dark={dark} />
            )}
            {activeTab === 'betreuungen' && (
              <BetreuungenTab
                user={user}
                dark={dark}
                // Automatisch gesetztes PB-Startdatum sofort in die Seitenleiste übernehmen.
                onPbStartdatumChanged={startdatum => { if (user) onSaved?.({ ...user, startdatum }); }}
              />
            )}
            {activeTab === 'medien-dokumente' && (
              <MedienTab user={user} dark={dark} />
            )}
            {activeTab === 'kommunikation' && (
              <KommunikationTab user={user} dark={dark} />
            )}
            {activeTab === 'aufgaben-infos' && (
              <AufgabenNotizenTab user={user} dark={dark} noticeTerm="infos" />
            )}
            {activeTab === 'bewertungen' && (
              <BewertungenTab user={user} dark={dark} />
            )}
            {activeTab === 'protokoll' && canReadProtokoll && (
              <ProtokollTab user={user} dark={dark} />
            )}
          </div>
        </section>

      </div>
    </div>
  );
}

/* ─── Pill tab bar ──────────────────────────────────────────────────────────── */
function TabBar({
  active, onChange, dark, isMobile = false, hasUser, userId = null, careRecipient = true,
}: {
  /** users.id der Person: Basis für echte Tab-Links (Strg/Cmd-Klick, neuer Tab). */
  userId?: number | null;
  active: PbTabId;
  onChange: (tab: PbTabId) => void;
  dark: boolean;
  isMobile?: boolean;
  hasUser: boolean;
  careRecipient?: boolean;
}) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const { hasPermission } = usePermissions();
  // For new profiles (no user id), hide dashboard tab
  const visible = careRecipient ? PB_TABS : PB_TABS.filter(tab => !CARE_ONLY_TABS.has(tab.id));
  const baseTabs = hasUser ? visible : visible.filter(tab => tab.id !== 'dashboard');
  const tabs = hasUser && hasPermission(PROTOKOLL_PERMISSION) ? [...baseTabs, PROTOKOLL_TAB] : baseTabs;
  return (
    <div style={{
      display: 'flex',
      flexWrap: isMobile ? 'nowrap' : 'wrap',
      overflowX: isMobile ? 'auto' : 'visible',
      WebkitOverflowScrolling: 'touch',
      gap: 6, padding: 4,
      background: t.card, border: `1px solid ${t.border}`, borderRadius: 10,
      boxShadow: dark ? 'none' : '0 1px 2px rgba(16,24,40,0.05)',
      scrollbarWidth: 'thin',
    }}>
      {tabs.map(({ id, labelKey }) => {
        const on = active === id;
        return (
          <AppLink
            key={id}
            to={buildPath('pflege', String(userId ?? ''), id)}
            onClick={(e) => { if (!isNewTabClick(e)) { e.preventDefault(); onChange(id); } }}
            aria-current={on ? 'page' : undefined}
            style={{
              border: 'none', cursor: 'pointer',
              padding: isMobile ? '9px 14px' : '8px 14px', borderRadius: 8,
              background: on ? BRAND_LIGHT : 'transparent',
              color: on ? BRAND_DARK : t.text,
              fontSize: 13, fontWeight: on ? 600 : 500,
              fontFamily: 'Outfit,sans-serif',
              transition: 'background 0.12s',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              minHeight: isMobile ? 38 : undefined,
              textDecoration: 'none', display: 'inline-block',
            }}
            onMouseEnter={e => { if (!on) (e.currentTarget as HTMLAnchorElement).style.background = t.hover; }}
            onMouseLeave={e => { if (!on) (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
          >
            {tr(labelKey)}
          </AppLink>
        );
      })}
    </div>
  );
}
